import React, { useEffect, useRef } from "react";
import { useDiagramSvg, DiagramSvgStatus } from "../../hooks/useDiagramSvg";

interface DiagramImageProps {
  projectId: string;
  alt?: string;
  /** Bump to force a re-resolve (e.g. a manual "update image" action). */
  refreshKey?: number;
}

/**
 * Renders the project's architecture diagram as an inline image.
 *
 * A thin presentational wrapper over {@link useDiagramSvg} — the reusable entry
 * point that resolves the diagram SVG URL (triggering server-side
 * render-if-stale + cache reuse). Used by {@link MarkdownRenderer} when markdown
 * references the canonical diagram path; pass an incrementing `refreshKey` to
 * force a re-resolve (e.g. from a manual "update image" control).
 */
const DiagramImage: React.FC<DiagramImageProps> = ({
  projectId,
  alt,
  refreshKey,
}) => {
  const { url, status, refresh } = useDiagramSvg(projectId);

  // Re-resolve when the caller bumps refreshKey (skip the initial mount, which
  // the hook already resolves on its own).
  const prevRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (prevRefreshKey.current !== refreshKey) {
      prevRefreshKey.current = refreshKey;
      refresh();
    }
  }, [refreshKey, refresh]);

  if (status === DiagramSvgStatus.IDLE) {
    return null;
  }

  if (status === DiagramSvgStatus.LOADING) {
    return (
      <span className="text-sm text-muted-foreground italic">
        Generating diagram image…
      </span>
    );
  }

  if (status === DiagramSvgStatus.ERROR || !url) {
    return (
      <span className="text-sm text-muted-foreground italic">
        Diagram image is unavailable. It may still be generating — try again
        shortly.
      </span>
    );
  }

  return (
    <img
      src={url}
      alt={alt ?? "Architecture diagram"}
      className="max-w-full h-auto"
    />
  );
};

export default DiagramImage;
